/*
 * Account Queries And Mutations
 *
 * Features:
 * - Shares global, team, current-user, and detail account reads
 * - Keeps account, contact-projection, My-list, and delete caches coherent after writes
 *
 * Implementation:
 * - All network calls continue through dataService
 * - Mutation identifiers are normalized or rejected before request execution
 * - Delete cleanup removes the active detail before list invalidation and router navigation
 *
 * Changes:
 * - 2026-08-06: Added the account half of the TanStack Query server-cache pilot
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import type { Account } from '../models/Account';
import { request } from '../services/dataService';
import { accountKeys, contactKeys, normalizePositiveId, normalizeTeamId, requirePositiveId } from './queryKeys';

interface CreateAccountPayload {
  userId: number;
  name: string;
  teamId: number;
  data: string;
}

interface UpdateAccountPayload {
  id: number;
  name: string;
  data?: string;
}

interface AccountIdentityPayload {
  id: number;
}

interface UserAccountPayload extends AccountIdentityPayload {
  userId: number;
}

export function useGlobalAccountsQuery(enabled = true) {
  return useQuery<Account[]>({
    queryKey: accountKeys.global(),
    queryFn: () => request(API_ENDPOINTS.GET_ACCOUNTS),
    enabled,
  });
}

export function useTeamAccountsQuery(teamId: string | number | null | undefined) {
  const normalizedTeamId = normalizeTeamId(teamId);
  return useQuery<Account[]>({
    queryKey: accountKeys.team(normalizedTeamId),
    queryFn: () => request(API_ENDPOINTS.GET_TEAM_ACCOUNTS, { teamId: normalizedTeamId }),
    enabled: normalizedTeamId !== null,
  });
}

export function useUserAccountsQuery(userId: string | number | null | undefined) {
  const normalizedUserId = normalizePositiveId(userId);
  return useQuery<Account[]>({
    queryKey: accountKeys.user(normalizedUserId),
    queryFn: () => request(API_ENDPOINTS.GET_USER_ACCOUNTS, { userId: normalizedUserId }),
    enabled: normalizedUserId !== null,
  });
}

export function useAccountQuery(id: string | number | null | undefined) {
  const normalizedId = normalizePositiveId(id);
  return useQuery<Account | null>({
    queryKey: accountKeys.detail(normalizedId),
    queryFn: () => request(API_ENDPOINTS.GET_ACCOUNT, { id: normalizedId }),
    enabled: normalizedId !== null,
  });
}

export function useCreateAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAccountPayload) => request(API_ENDPOINTS.POST_ACCOUNT, {
      ...payload,
      userId: requirePositiveId(payload.userId, 'userId'),
      teamId: requirePositiveId(payload.teamId, 'teamId'),
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountKeys.lists() }),
  });
}

export function useUpdateAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateAccountPayload) => request(API_ENDPOINTS.PATCH_ACCOUNT, {
      ...payload,
      id: requirePositiveId(payload.id, 'id'),
    }),
    onSuccess: async (_data, payload) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: accountKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: accountKeys.detail(payload.id), exact: true }),
        queryClient.invalidateQueries({ queryKey: contactKeys.all }),
      ]);
    },
  });
}

export function useDeleteAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: AccountIdentityPayload) => request(API_ENDPOINTS.DELETE_ACCOUNT, {
      id: requirePositiveId(id, 'id'),
    }),
    onSuccess: async (_data, { id }) => {
      await queryClient.cancelQueries({ queryKey: accountKeys.detail(id), exact: true });
      queryClient.removeQueries({ queryKey: accountKeys.detail(id), exact: true });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: accountKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: contactKeys.all }),
      ]);
    },
  });
}

export function useAddUserAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UserAccountPayload) => request(API_ENDPOINTS.POST_USER_ACCOUNT, {
      ...payload,
      id: requirePositiveId(payload.id, 'id'),
      userId: requirePositiveId(payload.userId, 'userId'),
    }),
    onSuccess: (_data, { userId }) => queryClient.invalidateQueries({
      queryKey: accountKeys.user(userId),
      exact: true,
    }),
  });
}

export function useRemoveUserAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UserAccountPayload) => request(API_ENDPOINTS.DELETE_USER_ACCOUNT, {
      ...payload,
      id: requirePositiveId(payload.id, 'id'),
      userId: requirePositiveId(payload.userId, 'userId'),
    }),
    onSuccess: (_data, { userId }) => queryClient.invalidateQueries({
      queryKey: accountKeys.user(userId),
      exact: true,
    }),
  });
}
